import {
  SPEECH_LANGUAGE_LABELS,
  SpeechLanguage,
  VOICE_NAME_MAX,
  VOICE_REF_MAX_SEC,
  VOICE_REF_MIN_SEC,
  type Voice,
} from "@studio/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MicVocal, Pause, Play, Plus, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { voiceClipBlocker } from "@/lib/composer";
import { cn, formatClock, formatDuration } from "@/lib/format";
import { Button, Sheet, Spinner } from "./ui";

const field =
  "w-full rounded-[var(--radius-control)] border border-line bg-desk px-3 text-[16px] outline-none placeholder:text-mute/70 focus-visible:border-mute";

/** Voices you saved: a name over a few seconds of someone speaking, which speech can then clone. */
export function Voices() {
  const queryClient = useQueryClient();
  const voices = useQuery({ queryKey: ["voices"], queryFn: api.voices });
  const [editing, setEditing] = useState<Voice | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (voice: Voice) => api.deleteVoice(voice.id).then(() => voice),
    onSuccess: (voice) => {
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ["voices"] });
      toast.success(`Deleted ${voice.name}`);
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-[17px] font-semibold">Voices</h2>
          <p className="mt-1 max-w-[62ch] text-[14px] text-mute">
            A few seconds of someone speaking, kept under a name. Speech can then say anything in that voice,
            here or from a script. Ask before you use someone else's.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditing("new")}>
          <Plus className="size-4" aria-hidden />
          Add voice
        </Button>
      </div>

      {voices.isLoading ? <Spinner /> : null}
      {voices.data && voices.data.length === 0 ? (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-3 rounded-[var(--radius-control)] border border-dashed border-line px-4 py-4 text-left text-[14px] text-mute hover:border-mute hover:text-paper"
        >
          <MicVocal className="size-5 shrink-0" aria-hidden />
          No voices yet. Add {VOICE_REF_MIN_SEC} to {VOICE_REF_MAX_SEC} seconds of someone speaking and speech
          can use that voice.
        </button>
      ) : null}

      {voices.data?.length ? (
        <ul className="flex flex-col divide-y divide-line/60 rounded-[var(--radius-control)] border border-line">
          {voices.data.map((voice) => (
            <VoiceRow
              key={voice.id}
              voice={voice}
              onEdit={() => setEditing(voice)}
              confirming={confirmDelete === voice.id}
              onAskDelete={() => setConfirmDelete(voice.id)}
              onKeep={() => setConfirmDelete(null)}
              onDelete={() => remove.mutate(voice)}
              deleting={remove.isPending}
            />
          ))}
        </ul>
      ) : null}

      {editing ? (
        <VoiceSheet
          voice={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ["voices"] });
          }}
        />
      ) : null}
    </section>
  );
}

function VoiceRow({
  voice,
  onEdit,
  confirming,
  onAskDelete,
  onKeep,
  onDelete,
  deleting,
}: {
  voice: Voice;
  onEdit: () => void;
  confirming: boolean;
  onAskDelete: () => void;
  onKeep: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const asset = useQuery({ queryKey: ["asset", voice.assetId], queryFn: () => api.asset(voice.assetId) });
  const meta = [
    formatDuration(voice.durationSec),
    voice.language === "auto" ? null : SPEECH_LANGUAGE_LABELS[voice.language],
    `saved ${formatClock(voice.createdAt)}`,
  ].filter(Boolean);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
      <PlayButton url={asset.data?.url ?? null} label={`Play ${voice.name}`} />
      <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
        <p className="truncate text-[15px] font-medium">{voice.name}</p>
        <p className="tabular text-[13px] text-mute">{meta.join(" · ")}</p>
        {voice.referenceText ? (
          <p className="line-clamp-1 text-[13px] text-mute/80">“{voice.referenceText}”</p>
        ) : null}
      </button>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="quiet" onClick={onEdit}>
          Edit
        </Button>
        {confirming ? (
          <>
            <Button size="sm" variant="danger" onClick={onDelete} disabled={deleting}>
              Delete {voice.name}
            </Button>
            <Button size="sm" variant="quiet" onClick={onKeep}>
              Keep
            </Button>
          </>
        ) : (
          <Button size="sm" variant="quiet" onClick={onAskDelete}>
            Delete
          </Button>
        )}
      </div>
    </li>
  );
}

/** Hear a voice without leaving the page: the clip on the server, or the file about to be uploaded. */
function PlayButton({ url, label }: { url: string | null; label: string }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={playing ? "Pause" : label}
        disabled={!url}
        onClick={() => {
          const el = audio.current;
          if (!el) return;
          if (el.paused) void el.play();
          else el.pause();
        }}
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-film text-film-ink hover:bg-[#f7cf4f] disabled:opacity-40"
      >
        {playing ? (
          <Pause className="size-4" aria-hidden />
        ) : (
          <Play className="size-4 translate-x-px" aria-hidden />
        )}
      </button>
      {/* biome-ignore lint/a11y/useMediaCaption: a voice sample has no captions */}
      <audio
        ref={audio}
        src={url ?? undefined}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </>
  );
}

/** How long a file plays, read by the browser before anything is uploaded. */
function clipSeconds(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const probe = new Audio();
    probe.preload = "metadata";
    probe.onloadedmetadata = () => resolve(Number.isFinite(probe.duration) ? probe.duration : null);
    probe.onerror = () => resolve(null);
    probe.src = url;
  });
}

function fileStem(name: string): string {
  return name.replace(/\.[^.]+$/, "").slice(0, VOICE_NAME_MAX);
}

/** Add a voice (a file, a name and what it says) or change the name, transcript and language of one. */
function VoiceSheet({
  voice,
  onClose,
  onSaved,
}: {
  voice: Voice | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(voice?.name ?? "");
  const [referenceText, setReferenceText] = useState(voice?.referenceText ?? "");
  const [language, setLanguage] = useState<SpeechLanguage>(voice?.language ?? "auto");
  const [clip, setClip] = useState<{ file: File; url: string; durationSec: number | null } | null>(null);
  const saved = useQuery({
    queryKey: ["asset", voice?.assetId],
    queryFn: () => api.asset(voice?.assetId as string),
    enabled: Boolean(voice),
  });

  // The preview plays from the file itself, so the browser keeps it until this sheet closes.
  useEffect(() => {
    if (!clip) return;
    return () => URL.revokeObjectURL(clip.url);
  }, [clip]);

  const take = async (file: File) => {
    const url = URL.createObjectURL(file);
    setClip({ file, url, durationSec: await clipSeconds(url) });
    if (!name.trim()) setName(fileStem(file.name));
  };

  const dropzone = useDropzone({
    accept: { "audio/*": [] },
    multiple: false,
    disabled: Boolean(voice),
    onDrop: (files) => {
      const file = files[0];
      if (file) void take(file);
    },
    onDropRejected: () => toast.error("A voice is cloned from a sound file."),
  });

  const blocker = clip ? voiceClipBlocker(clip.durationSec) : null;

  const save = useMutation({
    mutationFn: async () => {
      if (voice) return api.updateVoice(voice.id, { name: name.trim(), referenceText, language });
      if (!clip) throw new Error("Choose a sound file first.");
      const asset = await api.upload(clip.file);
      return api.createVoice({ name: name.trim(), assetId: asset.id, referenceText, language });
    },
    onSuccess: (stored) => {
      toast.success(voice ? `Saved ${stored.name}` : `Added ${stored.name}`);
      onSaved();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && onClose()}
      title={voice ? `Edit ${voice.name}` : "Add a voice"}
      description={`${VOICE_REF_MIN_SEC} to ${VOICE_REF_MAX_SEC} seconds of one person speaking, without music behind it.`}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {voice ? (
          <div className="flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-desk px-3 py-2.5">
            <PlayButton url={saved.data?.url ?? null} label={`Play ${voice.name}`} />
            <span className="tabular text-[13px] text-mute">{formatDuration(voice.durationSec)}</span>
            <span className="text-[13px] text-mute">
              The clip itself cannot be swapped — add another voice.
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div
              {...dropzone.getRootProps()}
              className={cn(
                "flex cursor-pointer flex-col items-center gap-1.5 rounded-[var(--radius-control)] border border-dashed border-line px-4 py-6 text-center hover:border-mute",
                dropzone.isDragActive && "border-film",
              )}
            >
              <input {...dropzone.getInputProps()} />
              <Upload className="size-5 text-mute" aria-hidden />
              <p className="text-[14px] text-paper">
                {clip ? clip.file.name : "Drop a sound here, or choose a file"}
              </p>
              <p className="text-[13px] text-mute">
                {VOICE_REF_MIN_SEC}–{VOICE_REF_MAX_SEC} seconds, one speaker, as clean as you have.
              </p>
            </div>
            {clip ? (
              <div className="flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-desk px-3 py-2.5">
                <PlayButton url={clip.url} label="Play the clip" />
                <span className="tabular text-[13px] text-mute">{formatDuration(clip.durationSec)}</span>
                {blocker ? <span className="text-[13px] text-danger">{blocker}</span> : null}
              </div>
            ) : null}
          </div>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-mute">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={VOICE_NAME_MAX}
            placeholder="Anna, narrator"
            className={cn(field, "h-10")}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-mute">What the clip says</span>
          <textarea
            value={referenceText}
            onChange={(e) => setReferenceText(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Optional. Typing the words in the clip makes the clone closer."
            className={cn(field, "resize-none py-2 leading-6")}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-mute">Language of the clip</span>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as SpeechLanguage)}
            className={cn(field, "h-10")}
          >
            {SpeechLanguage.options.map((code) => (
              <option key={code} value={code}>
                {SPEECH_LANGUAGE_LABELS[code]}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center justify-end gap-2 border-t border-line/60 pt-3">
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={save.isPending || !name.trim() || (!voice && (!clip || Boolean(blocker)))}
          >
            {save.isPending ? "Saving…" : voice ? "Save voice" : "Add voice"}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
